import React from "react";
import { clsx } from "clsx";

// Constants for button styling
const BASE_STYLES = "inline-flex items-center justify-center font-medium rounded-lg transition-colors cursor-pointer";

const VARIANT_STYLES = {
  primary: "bg-gray-950 hover:bg-gray-600 text-white focus:ring-gray-500",
  secondary: "border-2 border-gray-950 text-black hover:bg-gray-200",
  danger: "bg-red-500 hover:bg-red-600 text-white focus:ring-red-500",
} as const;

const SIZE_STYLES = {
  sm: "px-2 py-1 text-sm",
  md: "px-4 py-2 text-sm", 
  lg: "px-6 py-3 text-base",
} as const;

const LOADING_ICON_SIZE = "w-4 h-4";
const DISABLED_STYLES = "opacity-50 cursor-not-allowed";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof VARIANT_STYLES;
  size?: keyof typeof SIZE_STYLES;
  loading?: boolean;
  children: React.ReactNode;
}

// Loading spinner component
const LoadingSpinner = () => (
  <svg
    className={clsx(LOADING_ICON_SIZE, "mr-2 animate-spin")}
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export const Button = ({
  variant = "primary",
  size = "md",
  loading = false,
  children,
  className = "",
  disabled,
  ...props
}: ButtonProps) => {
  const isDisabled = disabled || loading;

  const buttonClasses = clsx(
    BASE_STYLES,
    VARIANT_STYLES[variant],
    SIZE_STYLES[size],
    isDisabled && DISABLED_STYLES,
    className
  );

  return (
    <button
      className={buttonClasses}
      disabled={isDisabled}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {children}
    </button>
  );
};
